import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { UiTab, UiTable, UiRangePicker, UiSelect } from "../../../components";
import { PENALTY_TAB_DATA, PENALTY_COLUMNS, REFUND_COLUMNS, PAY_PENALTY, REFUND_TO_CUSTOMER } from "./config";
import "./style.scss";
import { TRANSACTIONMANAGEMENT, LAWYERMANAGEMENT, CUSTOMERMANAGEMENT } from "../../../apiServices/endpoints";
import * as api from "../../../actions";
import { searchInitiateDelayTime, tablePageSizeOptions } from "../../../utils/appUtils";
import { loaderReducer } from "../../../reducer/loader.reducer";

const Penalty = () => {
    const dispatch = useDispatch();
    const debounceTimeOut = useRef(null);
    const [penaltyData, setPenaltyData] = useState([]);
    const [refundData, setRefundData] = useState([]);
    const [filter, setFilter] = useState({
        activeTab: "PENALTY", activeTabKey: "1", page: 0, size: 10, searchFilter: "", searchTermValue: "",
        month: null, monthString: "", fromDate: "", toDate: ""
    });
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    const getLawyerName = (search) => api.get(`${LAWYERMANAGEMENT}/get_lawyer_name?lawyerSearch=${search}`);
    const getCustomerName = (search) => api.get(`${CUSTOMERMANAGEMENT}/get_customer_name?customerSearch=${search}`);
    const getDataList = (filter, paymentCategory) => api.get(`${TRANSACTIONMANAGEMENT}/penalty_refund?paymentCategory=${paymentCategory}&page=${filter?.page}&size=${filter?.size}${filter?.searchTermValue ? `&search=${filter?.searchTermValue}` : ""}${filter?.monthString ? `&month=${filter?.monthString}` : ""}${filter?.fromDate && filter?.toDate ? `&fromDate=${filter?.fromDate}&toDate=${filter?.toDate}` : ""}`);

    const { isFetching: isFetchingLawyerNames } = useQuery(
        ["FETCH_LAWYER_NAMES", searchValue],
        () => getLawyerName(searchValue),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const options = response?.result?.lawyerNameDropdownResponses?.map(item => ({
                        label: item?.fullName,
                        value: item?.userId,
                        key: item?.userId
                    })) || [];
                    setSearchOptions(options);
                } else {
                    setSearchOptions([]);
                }
            },
            enabled: filter?.activeTabKey === "1" && !!searchValue,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCustomerNames } = useQuery(
        ["FETCH_CUSTOMER_NAMES", searchValue],
        () => getCustomerName(searchValue),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const options = response?.result?.customerNameDropdowns?.map(item => ({
                        label: item?.fullName,
                        value: item?.userId,
                        key: item?.userId
                    })) || [];
                    setSearchOptions(options);
                } else {
                    setSearchOptions([]);
                }
            },
            enabled: filter?.activeTabKey === "2" && !!searchValue,
            refetchOnWindowFocus: false
        }
    );

    const { data: dataResponse, isFetching } = useQuery(
        ["FETCH_PENALTY_REFUND_LIST", filter?.activeTabKey, filter?.page, filter?.size, filter?.searchTermValue, filter?.monthString, filter?.fromDate, filter?.toDate],
        () => {
            const paymentCategory = filter?.activeTabKey === "1" ? PAY_PENALTY : REFUND_TO_CUSTOMER;
            return getDataList(filter, paymentCategory);
        },
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const content = response?.result?.penaltyRefundResponse?.content || []; 
                    if (filter?.activeTabKey === "1") {
                        setPenaltyData(content);
                    } else {
                        setRefundData(content);
                    }
                } else {
                    if (filter?.activeTabKey === "1") {
                        setPenaltyData([]);
                    } else {
                        setRefundData([]);
                    }
                }
            },
            enabled: Boolean(filter?.page !== undefined && filter?.size),
            refetchOnWindowFocus: false
        }
    );

    const handleDropdownSearch = (value) => {
        if (debounceTimeOut?.current) {
            clearTimeout(debounceTimeOut?.current);
        }
        if (value) {
            debounceTimeOut.current = setTimeout(() => {
                setSearchValue(value);
            }, searchInitiateDelayTime);
        } else {
            setSearchValue("");
            setSearchOptions([]);
        }
    };

    const handleSelectSearch = (value) => {
        setFilter(prev => ({ ...prev, searchFilter: value, searchTermValue: value, page: 0 }));
    };

    const handleTabChange = (key) => {
        PENALTY_TAB_DATA?.forEach((tab) => {
            if (tab?.key === key) {
                const { content } = tab || {};
                setFilter(prev => ({ ...prev, activeTab: content }));
            }
        });
        setSearchOptions([]);
        setSearchValue("");
        setFilter(prev => ({ ...prev, activeTabKey: key, page: 0, size: 10, searchFilter: "", searchTermValue: "", month: null, monthString: "", fromDate: "", toDate: "" }));
    };

    const handleMonthChange = (month) => {
        if (month) {
            const monthValue = dayjs(month).format("YYYY-MM");
            setFilter(prev => ({ ...prev, month: month, monthString: monthValue, fromDate: "", toDate: "", page: 0 }));
        } else {
            setFilter(prev => ({ ...prev, month: null, monthString: "", page: 0 }));
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            const fromDate = dayjs(date?.[0]).format("YYYY-MM-DD");
            const toDate = dayjs(date?.[1]).format("YYYY-MM-DD");
            if (fromDate && toDate) {
                setFilter(prev => ({ ...prev, fromDate: fromDate, toDate: toDate, month: null, monthString: "", page: 0 }));
            }
        } else {
            setFilter(prev => ({ ...prev, fromDate: "", toDate: "", page: 0 }));
        }
    };

    const handlePagination = (page, size) => {
        setFilter(prev => ({ ...prev, page: page - 1, size: size }));
    };

    useEffect(() => {
        dispatch(loaderReducer(isFetching || isFetchingLawyerNames || isFetchingCustomerNames));
    }, [dispatch, isFetching, isFetchingLawyerNames, isFetchingCustomerNames]);

    const totalPenaltyCount = dataResponse?.result?.penaltyRefundResponse?.totalElements || 0;
    const shouldShowPenaltyPagination = totalPenaltyCount > 10;

    return (
        <div>
            <div className="flexible">
                <div>
                    <h6 style={{ padding: "10px", margin: 0, whiteSpace: "nowrap" }}>Penalty</h6>
                </div>
                <div className="flexible">
                    <DatePicker 
                        picker="month" 
                        format="MMM" 
                        value={filter?.month} 
                        style={{ height: 32, width: 150, borderRadius: 6 }} 
                        onChange={(month) => handleMonthChange(month)} 
                        disabledDate={(current) => current && current > dayjs().endOf("day")} 
                        data-testid="penalty-month-picker"
                    />
                    <UiRangePicker 
                        value={filter?.fromDate && filter?.toDate ? [dayjs(filter?.fromDate), dayjs(filter?.toDate)] : null} 
                        disabledDate={(current) => current && current > dayjs().endOf("day")} 
                        onChange={(date) => handleDateChange(date)} 
                        data-testid="penalty-range-picker"
                    />
                    <div style={{ width: "300px" }}>
                        <UiSelect
                            showSearch
                            value={filter?.searchFilter || undefined}
                            placeholder={filter?.activeTabKey === "1" ? "Search Lawyer Name / ID" : "Search Customer Name / ID"}
                            defaultActiveFirstOption={false}
                            showArrow={false}
                            filterOption={false}
                            onInput={(event) => handleDropdownSearch(event.target.value)}
                            onChange={handleSelectSearch}
                            notFoundContent={null}
                            options={searchOptions}
                            allowClear
                            style={{ width: "100%" }}
                            onClear={() => {
                                setFilter(prev => ({ ...prev, searchFilter: "", searchTermValue: "", page: 0 }));
                                setSearchOptions([]);
                                setSearchValue("");
                            }}
                        />
                    </div>
                </div>
            </div>
            <UiTab
                tabs={PENALTY_TAB_DATA}
                activeTabKey={filter?.activeTabKey}
                onChange={handleTabChange}
                count={dataResponse?.result?.penaltyRefundResponse?.totalElements || 0}
            />
            <UiTable
                columns={filter?.activeTabKey === "1" ? PENALTY_COLUMNS : REFUND_COLUMNS}
                dataSource={filter?.activeTabKey === "1" ? penaltyData : refundData}
                scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                pagination={!shouldShowPenaltyPagination ? false : {
                    current: filter?.page + 1,
                    pageSize: filter?.size || 10,
                    total: totalPenaltyCount,
                    pageSizeOptions: tablePageSizeOptions,
                    showSizeChanger: true,
                    onChange: handlePagination
                }}
            />
        </div>
    );
};

export default Penalty;
